import { FC, useContext, useEffect, useState } from 'react';
import { Close, Save } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Grid, TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Loader } from '..';
import { useQuery } from 'react-query';
import { getBuildNotes, postBuildNotes } from '../../fetch';
import { IBuildNotes } from '../../models';
import { useSnackbar } from 'notistack';
import { getUserInitials } from '../../helpers';
import { UserContext } from '../../context';

interface IBuildNotesProps {
  buildId: string;
}

export const BuildNotes: FC<IBuildNotesProps> = ({ buildId }) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);

  const {
    isLoading: isLoadingBuildNotes,
    data: buildNotes,
    refetch: getNotes
  } = useQuery<IBuildNotes, Error>(['getBuildNotes', buildId], () => getBuildNotes(buildId));

  useEffect(() => {
    if (buildNotes) {
      setNotes(buildNotes.notes);
    }
  }, [buildNotes]);

  const isDirty = () => {
    return notes !== buildNotes?.notes;
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      // default to central time zone
      const dateTimeString = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago'
      });
      const updatedNotes = `${notes} - ${dateTimeString}${
        user.fullName ? `, ${getUserInitials(user.fullName)}` : ''
      }`;
      await postBuildNotes(buildId, updatedNotes);
      setIsSaving(false);
      enqueueSnackbar('Success, note saved!', {
        variant: 'success'
      });
      getNotes();
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error saving note, please try again.`, {
        variant: 'error'
      });
      setIsSaving(false);
      console.log(error);
    }
  };

  return (
    <>
      {!isLoadingBuildNotes && !isSaving ? (
        <Grid container>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={6}
              variant="outlined"
              autoComplete="nope"
              label="Notes"
              name="notes"
              size="small"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <ButtonGroup className={classes.buttonGroup}>
              <Button
                disabled={!isDirty()}
                type="submit"
                startIcon={<Save />}
                variant="contained"
                color="primary"
                onClick={() => {
                  handleSave();
                }}
              >
                Save
              </Button>
              <Button
                disabled={!isDirty()}
                type="button"
                variant="contained"
                color="inherit"
                startIcon={<Close />}
                onClick={() => {
                  setNotes(buildNotes.notes);
                }}
              >
                Reset
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      ) : (
        <Box mt={1} minHeight="4rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            {isLoadingBuildNotes ? 'Loading...' : 'Saving...'}
          </Loader>
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  buttonGroup: {
    paddingTop: '16px'
  }
}));
